import React, { useMemo } from 'react';
import { find, findIndex } from 'lodash';
import PropTypes from 'prop-types';
import { Alert, Box, Button, CardContent, Grid, Link, List, ListItem, Paper, Typography } from '@availity/element';

import { Footer, BackupCodeDisplay, AvailityHeaderLogo, MessageDisplay } from '@/components';
import { TwoFAIcon04 } from '@/static';
import { useFRCallback } from '@/hooks';
import { useAppContext } from '@/context';
import { JOURNEY_2FA_CHALLENGE } from '@/global';

const faqLink =
  'https://essentials.availity.com/availity/help-open/source/portal_providers/account_administration/2-step/_topics/c_faqs_for_2step_authentication.html';

function VoiceSMSWarning() {
  return (
    <Alert severity="warning" icon={false} sx={{ marginBottom: 2 }}>
      <Typography variant="h3" component="h2" sx={{ marginBottom: 2 }}>
        Don&apos;t lose access, update now!
      </Typography>
      <Typography variant="h4" component="h3" sx={{ marginBottom: 2 }}>
        Text and Voice authentication are going away soon.
      </Typography>
      <Typography variant="subtitle1">
        Make sure to set up a new authentication method using an authenticator app (like Google Authenticator) to
        continue accessing Essentials.
      </Typography>
    </Alert>
  );
}

function SidebarContent({ ...props }) {
  const { showBackupCodes } = props;

  return (
    <>
      <Box textAlign="center" mb={2}>
        <img
          src={TwoFAIcon04}
          alt="2FA Icon"
          style={{
            maxWidth: '45%',
          }}
        />
      </Box>
      {showBackupCodes ? (
        <Box textAlign="center">
          <Typography variant="h5" component="h3" mb={2}>
            You&apos;re all set!
          </Typography>
        </Box>
      ) : (
        <>
          <Box textAlign="center">
            <Typography variant="h5" component="h3" mb={2}>
              Update Authentication Later
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle1">
              To update your 2-Step Authentication method at a later time, follow the directions below:
            </Typography>
          </Box>
          <List
            component="ol"
            sx={{
              listStyleType: 'decimal',
              listStylePosition: 'outside',
              paddingX: 3,
            }}
          >
            <ListItem divider={false} sx={{ display: 'list-item' }}>
              <Typography variant="subtitle1">From the homepage, open My Account.</Typography>
            </ListItem>
            <ListItem divider={false} sx={{ display: 'list-item' }}>
              <Typography variant="subtitle1">Click the Security Tab.</Typography>
            </ListItem>
            <ListItem divider={false} sx={{ display: 'list-item' }}>
              <Typography variant="subtitle1">Click Update 2-step authentication.</Typography>
            </ListItem>
          </List>
        </>
      )}
      <Box textAlign="center" mt={3}>
        <Link
          href={faqLink}
          target="_blank"
          loadApp={false}
          iconPosition="end"
          rel="noopener noreferrer"
          aria-label="Get answers to common questions (opens PDF in a new window)"
        >
          2-Step Authentication FAQs
        </Link>
      </Box>
    </>
  );
}

SidebarContent.propTypes = {
  showBackupCodes: PropTypes.bool,
};

function NavigationRow({ ...props }) {
  const { hasEnrollButton, handleNavigation, journeyId } = props;

  // NOTE: due to FR bug, in the short term, cannot render the 'update now' button during the step-up flow. can only be rendered in login flow.
  return hasEnrollButton && journeyId !== JOURNEY_2FA_CHALLENGE ? (
    <Box textAlign="right" mt="auto">
      <Button color="secondary" sx={{ marginLeft: 1 }} onClick={() => handleNavigation()}>
        Continue
      </Button>
      <Button color="primary" onClick={() => handleNavigation('enroll')} sx={{ marginLeft: 1 }}>
        Update Now
      </Button>
    </Box>
  ) : (
    <Box textAlign="right" mt="auto">
      <Button color="primary" sx={{ marginLeft: 1 }} onClick={() => handleNavigation()}>
        Continue
      </Button>
    </Box>
  );
}

NavigationRow.propTypes = {
  hasEnrollButton: PropTypes.bool,
  handleNavigation: PropTypes.func.isRequired,
  journeyId: PropTypes.string,
};

function TwoFACompleteWarning({ ...props }) {
  const { stepToRender, setCurrentStep, journeyId, showBackupCodes } = props;
  const { setMessages } = useAppContext();

  const {
    callbacks,
    confirmationCallbackIndex,
    confirmationCallbackOptions = [],
    textOutputCallback,
  } = useFRCallback(stepToRender);

  const backupCodes = useMemo(() => {
    let output = [];
    if (textOutputCallback) {
      const textOutputArray = find(
        textOutputCallback.output,
        (outputObj) => outputObj?.name?.toLowerCase() === 'message'
      );

      // NOTE: if no codes present, the value will be 'default message'; in this case return an empty array.
      const unparsedOutput = textOutputArray?.value?.toLowerCase() !== 'default message' && textOutputArray?.value;

      output = unparsedOutput ? JSON.parse(unparsedOutput) : [];
    }

    return output;
  }, [textOutputCallback]);

  const hasEnrollButton = useMemo(
    () => confirmationCallbackOptions.indexOf('enroll') >= 0,
    [confirmationCallbackOptions]
  );

  const handleNavigation = (selectedNavigation) => {
    setMessages([]);

    const nextStep = stepToRender;
    const newCallbacks = [...callbacks];

    if (selectedNavigation === 'enroll') {
      const newDeviceIndex = findIndex(confirmationCallbackOptions, (button) => button?.toLowerCase() === 'enroll');

      newCallbacks[confirmationCallbackIndex].input[0] = {
        ...newCallbacks[confirmationCallbackIndex].input[0],
        value: newDeviceIndex,
      };

      // set the newCallbacks in the newStep
      Object.assign(nextStep.payload.callbacks, newCallbacks);
    }

    // pass in same step to go to the next step;
    setCurrentStep({ ...stepToRender });
  };

  return (
    <Grid
      id="2fa-confirmation-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        id="2fa-confirmation-page-content-grid"
        container
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        mt="auto"
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper
            variant="elevation"
            elevation={6}
            sx={{ width: { xs: '350px', sm: '500px', md: '750px', lg: '1000px' } }}
          >
            <CardContent>
              <Grid display="flex" flexDirection="row" flexWrap="wrap">
                <Grid size={{ sm: 12, md: 4 }} sx={{ marginBottom: { xs: 2, md: 0 }, paddingRight: 2 }}>
                  <SidebarContent showBackupCodes={showBackupCodes} />
                </Grid>
                <Grid
                  display="flex"
                  flexDirection="column"
                  size={{ md: 8 }}
                  sx={{ borderLeft: { sm: 0, md: 1 }, borderColor: { md: '#D7DAE0' } }}
                >
                  <Box pl={2}>
                    <Typography variant="h1" mb={1}>
                      Confirmation
                    </Typography>
                  </Box>
                  <Grid display="flex" flexDirection="column" flexGrow={1} pl={2}>
                    {showBackupCodes ? (
                      <Box pt={2} mb={4}>
                        <VoiceSMSWarning />
                        <Typography variant="subtitle1" mb={3}>
                          If you do not have access to your device, you can use a backup code to access your account.
                        </Typography>
                        <Typography variant="subtitle1" mb="2">
                          <Box component="span" sx={{ fontWeight: 'bold' }}>
                            Backup Codes
                          </Box>{' '}
                          - Each code can be used only one time.
                        </Typography>
                        <Grid display="flex" flexWrap="wrap" my={4}>
                          <BackupCodeDisplay backupCodes={backupCodes} />
                        </Grid>
                        <Grid>
                          <MessageDisplay />
                        </Grid>
                        <Typography mb={4}>
                          Keep these backup codes in a safe place and do not share with other users.
                        </Typography>
                      </Box>
                    ) : (
                      <Box pt={2} mb={4}>
                        <VoiceSMSWarning />
                      </Box>
                    )}
                    <NavigationRow
                      hasEnrollButton={hasEnrollButton}
                      handleNavigation={handleNavigation}
                      journeyId={journeyId}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Paper>
        </Grid>
      </Grid>
      <Footer />
    </Grid>
  );
}

TwoFACompleteWarning.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  journeyId: PropTypes.string,
  showBackupCodes: PropTypes.bool.isRequired,
};

export default TwoFACompleteWarning;
